import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { UNNAMED_INSTITUTION } from '@steward-app/steward-types';
import {
  loadPlaid, readInflight, clearInflight, saveCompletion, saveExitError,
  plaidExitErrorMessage, logPlaidExitError,
} from '../lib/plaidLink';
import { inAppPath } from '../lib/inAppPath';

/**
 * Where to land when the in-flight record doesn't say. That is a direct visit, a lost
 * session, or a record stashed before `returnTo` existed (#579) — in every one of those
 * the connections page is the safe answer, because it is the surface that can show any
 * connection whatever state it ended up in.
 */
const CONNECTIONS_PATH = '/settings/connections';

/**
 * Only in-app absolute paths are honoured. `returnTo` round-trips through sessionStorage,
 * which is same-origin but still not something to hand to `navigate()` unchecked: a value
 * starting with `//` or a scheme would take the user off-site, and this page runs
 * immediately on load with no user interaction behind it. Anything else falls back.
 *
 * `/\host` is rejected alongside `//host`: browsers normalise the backslash to a slash,
 * so it resolves cross-origin just the same, and `navigate()` throws a SecurityError that
 * would strand the user on this chrome-less page with no way out.
 */
function safeReturnTo(path: string | undefined): string {
  // The rule (including why tabs and newlines are stripped before the check) lives in
  // `inAppPath`, shared with the firm assistant's link rendering.
  return inAppPath(path) ?? CONNECTIONS_PATH;
}

/**
 * `/plaid-oauth` — the return target for OAuth institutions (e.g. Charles Schwab).
 *
 * Plaid redirects the browser tab here (with `?oauth_state_id=...`) after the user
 * authenticates at the institution. We re-create Link with the SAME link token stashed
 * before the redirect plus `receivedRedirectUri: window.location.href`, which makes Link
 * pick up exactly where it left off and fire `onSuccess`. We then hand the result back via
 * sessionStorage and navigate to the page the flow STARTED from (`returnTo`, #579) — so
 * the exchange / overage-confirm / resync logic (`lib/useConnectAccount`, which owns the
 * API client and the confirm modal) runs in one place rather than being duplicated here,
 * and the user ends up looking at the surface they left rather than always at Settings.
 *
 * This page is intentionally chrome-less and calls no façade API itself: without a stashed
 * in-flight record (a direct visit, an expired session tab, a double-back) there is nothing
 * to resume, so it just bounces to the connections page.
 */
export function PlaidOAuthReturn() {
  const navigate = useNavigate();
  const started = useRef(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Guard against React StrictMode's double-invoke and any re-render: resume once.
    if (started.current) return;
    started.current = true;

    const inflight = readInflight();
    const hasOauthState = new URLSearchParams(window.location.search).has('oauth_state_id');

    // Nothing to resume (direct visit / lost session) — return to connections.
    if (!inflight || !hasOauthState) {
      clearInflight();
      navigate(CONNECTIONS_PATH, { replace: true });
      return;
    }

    // Captured before Link runs: the handlers below fire after `clearInflight()`.
    const returnTo = safeReturnTo(inflight.returnTo);

    let cancelled = false;
    void (async () => {
      try {
        await loadPlaid();
        if (cancelled) return;
        const handler = window.Plaid!.create({
          token: inflight.linkToken,
          receivedRedirectUri: window.location.href,
          onSuccess: (publicToken, metadata) => {
            // `workspaceId` is copied off the in-flight record, never resolved here
            // (#864). This page sits outside WorkspaceProvider on purpose, so it has no
            // active workspace of its own, and the one thing it could reach for is
            // `localStorage`'s `steward-app:workspaceId` — which is exactly the value a
            // second tab may have rewritten while the browser was away at the
            // institution. The in-flight record was written inside the provider at the
            // moment the flow started, which is the only moment the answer is certain.
            const workspaceId = inflight.workspaceId;
            if (inflight.kind === 'relink') {
              saveCompletion({ kind: 'relink', connId: inflight.connId, workspaceId });
            } else {
              const institutionName = metadata.institution?.name ?? UNNAMED_INSTITUTION;
              // The quote rides across verbatim for the same reason (steward-app#2231): it is the
              // price the person agreed to before Link opened, not one this page could know.
              saveCompletion({ kind: 'connect', publicToken, institutionName, workspaceId, quote: inflight.quote });
            }
            clearInflight();
            navigate(returnTo, { replace: true });
          },
          // User abandoned the OAuth flow (`err` null — a genuine cancel, stay silent)
          // OR Link errored (`err` populated, F3). Either way, drop the in-flight
          // record and go back; there's nothing to complete. On a real failure, this
          // page navigates away before it can show anything itself, so log it here and
          // stash the message for ConnectedAccounts to surface on the next render
          // (same handoff mechanism as a successful completion).
          onExit: (err, metadata) => {
            if (err) {
              logPlaidExitError(err, metadata);
              saveExitError(plaidExitErrorMessage(err));
            }
            clearInflight();
            navigate(returnTo, { replace: true });
          },
        });
        handler.open();
      } catch {
        if (cancelled) return;
        // Link couldn't be resumed (e.g. CDN load failed). Surface a quiet message with a
        // manual way back rather than stranding the user on a blank page.
        clearInflight();
        setError('We could not finish connecting your account. Please return and try again.');
      }
    })();

    return () => { cancelled = true; };
  }, [navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-cream px-6">
      {error ? (
        <div className="text-center">
          <div className="text-slate text-[13px] font-sans mb-4">{error}</div>
          {/* Deliberately the connections page rather than `returnTo`: the flow has
              already failed, and that is the surface showing connection state and
              offering a retry. It is also the only destination this label is honest
              about. */}
          <button
            onClick={() => navigate(CONNECTIONS_PATH, { replace: true })}
            className="inline-flex items-center px-5 py-2 text-[11px] font-bold rounded-token border-none cursor-pointer bg-navy text-cream uppercase tracking-[0.1em] font-sans"
          >
            Back to connections
          </button>
        </div>
      ) : (
        <div className="text-slate text-[13px] font-sans">Finishing secure connection…</div>
      )}
    </div>
  );
}
